import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL =
  window.__ENV?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8000';

interface EventFormData {
  title: string;
  description: string;
  event_type: 'run' | 'flight' | 'other';
  privacy_mode: 'public' | 'private';
  start_time: string;
  athlete_name?: string;
  event_image_url?: string;
  gofundme_link?: string;
  openai_api_key?: string;
}

// ── Narration cost estimate ───────────────────────────────────────────────────
// gpt-4o-mini pricing (USD)
const INPUT_PRICE_PER_TOKEN  = 0.15  / 1_000_000;
const OUTPUT_PRICE_PER_TOKEN = 0.60  / 1_000_000;
const NARRATION_INTERVAL_S   = 30;   // cache TTL ≈ client poll interval
// Prompt skeleton + telemetry snapshot (~120 tokens)
const BASE_INPUT_TOKENS      = 120;
// Average narration response: 1–2 sentences ≈ 50 tokens
const AVG_OUTPUT_TOKENS      = 50;

function estimateNarration(descChars: number, durationHours: number) {
  const descTokens       = Math.min(Math.ceil(descChars / 4), 100); // description truncated at 400 chars
  const inputPerCall     = BASE_INPUT_TOKENS + descTokens;
  const calls            = Math.max(1, Math.ceil((durationHours * 3600) / NARRATION_INTERVAL_S));
  const totalInput       = calls * inputPerCall;
  const totalOutput      = calls * AVG_OUTPUT_TOKENS;
  const totalCost        = totalInput * INPUT_PRICE_PER_TOKEN + totalOutput * OUTPUT_PRICE_PER_TOKEN;
  return { inputPerCall, calls, totalCost };
}

const CreateEvent: React.FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [hashCode, setHashCode] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [plannedDurationHours, setPlannedDurationHours] = useState(1);
  
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    event_type: 'run',
    privacy_mode: 'public',
    start_time: new Date().toISOString().slice(0, 16),
    athlete_name: '',
    event_image_url: '',
    gofundme_link: '',
    openai_api_key: '',
  });


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const payload: Record<string, unknown> = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
      };
      // Only send optional strings if the user actually provided them
      if (!payload.openai_api_key || (payload.openai_api_key as string).trim() === '') {
        delete payload.openai_api_key;
      }
      if (!payload.athlete_name || (payload.athlete_name as string).trim() === '') {
        delete payload.athlete_name;
      }

      const response = await axios.post(`${API_BASE_URL}/events`, payload);
      
      setHashCode(response.data.hash_share_id);
      setPairCode(response.data.pair_code);
      setShowModal(true);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  const handleCopyHashCode = () => {
    navigator.clipboard.writeText(hashCode);
    alert('Share token copied to clipboard!');
  };

  const handleCopyPairCode = () => {
    navigator.clipboard.writeText(pairCode);
    alert('Pairing code copied to clipboard!');
  };

  const handleGoToEvent = () => {
    navigate(`/event/${hashCode}`);
  };

  return (
    <div className="container">
      <div className="header header-with-logo">
        <div className="header-left">
          <h1>Create New Event</h1>
          <p>Set up your activity streaming event</p>
        </div>
        <div className="header-right">
          <div className="wm-mark-box" aria-label="WM">
            <span className="wm-mark-text">WM</span>
          </div>
        </div>
      </div>
      
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Event Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="My Marathon Run"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Running for charity..."
            />
          </div>

          {/* Narration cost estimate — shown when a description is present */}
          {formData.description.trim().length > 0 && (() => {
            const est = estimateNarration(formData.description.length, plannedDurationHours);
            return (
              <div className="narration-cost-box">
                <div className="narration-cost-header">🎙️ AI Narration cost estimate</div>
                <div className="narration-cost-row">
                  <label htmlFor="planned-duration">Planned event duration</label>
                  <div className="narration-cost-duration-row">
                    <input
                      id="planned-duration"
                      type="number"
                      min={0.25}
                      max={24}
                      step={0.25}
                      value={plannedDurationHours}
                      onChange={e => setPlannedDurationHours(Math.max(0.25, Number(e.target.value)))}
                      className="narration-duration-input"
                    />
                    <span className="narration-duration-unit">hours</span>
                  </div>
                </div>
                <div className="narration-cost-stats">
                  <span>~{est.calls} narration calls</span>
                  <span className="narration-cost-sep">·</span>
                  <span>~{est.inputPerCall} input tokens / call</span>
                  <span className="narration-cost-sep">·</span>
                  <span className="narration-cost-total">
                    estimated total: <strong>${est.totalCost < 0.01 ? est.totalCost.toFixed(4) : est.totalCost.toFixed(3)}</strong>
                  </span>
                </div>
                <div className="narration-cost-note">
                  Based on gpt-4o-mini pricing ($0.15 / 1M input · $0.60 / 1M output).
                  Narration is generated once every 30 s and broadcast to all spectators —
                  cost does not scale with viewer count.
                </div>
              </div>
            );
          })()}

          <div className="form-group">
            <label htmlFor="openai_api_key">OpenAI API Key (optional)</label>
            <input
              type="password"
              id="openai_api_key"
              name="openai_api_key"
              value={formData.openai_api_key}
              onChange={handleChange}
              placeholder="sk-..."
              autoComplete="off"
            />
            <p className="hint">
              Provide your OpenAI API key to enable AI-powered live narration for spectators.
              The key is encrypted on the server and never shared.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="event_type">Activity Type *</label>
            <select
              id="event_type"
              name="event_type"
              value={formData.event_type}
              onChange={handleChange}
              required
            >
              <option value="run">Running</option>
              <option value="flight">Flying</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="athlete_name">
              {formData.event_type === 'flight' ? 'Pilot' : 'Athlete'} Name (optional)
            </label>
            <input
              type="text"
              id="athlete_name"
              name="athlete_name"
              value={formData.athlete_name}
              onChange={handleChange}
              placeholder={formData.event_type === 'flight' ? 'e.g. Peter' : 'e.g. Anna'}
              maxLength={100}
            />
            <p className="hint">
              Their initial will appear on the map marker. Defaults to {formData.event_type === 'flight' ? '"P"' : '"A"'} if left blank.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="privacy_mode">Privacy Mode *</label>
            <select
              id="privacy_mode"
              name="privacy_mode"
              value={formData.privacy_mode}
              onChange={handleChange}
              required
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="start_time">Start Time *</label>
            <input
              type="datetime-local"
              id="start_time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="event_image_url">Event Image URL</label>
            <input
              type="url"
              id="event_image_url"
              name="event_image_url"
              value={formData.event_image_url}
              onChange={handleChange}
              placeholder="https://example.com/banner.jpg"
            />
            <p className="hint">Recommended size: 1200×200 (or wider), PNG/JPG/WEBP.</p>
          </div>

          <div className="form-group">
            <label htmlFor="gofundme_link">GoFundMe Link (optional)</label>
            <input
              type="url"
              id="gofundme_link"
              name="gofundme_link"
              value={formData.gofundme_link}
              onChange={handleChange}
              placeholder="https://www.gofundme.com/f/..."
            />
          </div>
          
          <button type="submit" className="button button-primary" style={{ width: '100%' }}>
            Create Event
          </button>
        </form>
      </div>
      
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Event Created Successfully!</h2>
            
            <div className="warning">
              <strong>⚠️ Important:</strong> Save this pairing code now! It will only be shown once.
            </div>

            <p style={{ marginBottom: '0.5rem' }}>
              Pairing code (share this privately with the streamer):
            </p>
            
            <div className="hash-code">
              {pairCode}
            </div>
            
            <p style={{ marginBottom: '1rem' }}>
              Use this 8-character code to pair your mobile app with this event.
            </p>

            <p style={{ marginBottom: '0.5rem' }}>
              Public share link (safe to post):
            </p>
            <div className="hash-code" style={{ fontSize: '0.9rem' }}>
              {`${window.location.origin}/event/${hashCode}`}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                onClick={handleCopyPairCode}
                className="button button-primary"
                style={{ flex: 1 }}
              >
                Copy Pair Code
              </button>
              <button 
                onClick={handleGoToEvent}
                className="button button-primary"
                style={{ flex: 1 }}
              >
                Go to Event
              </button>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <button
                onClick={handleCopyHashCode}
                className="button"
                style={{ width: '100%' }}
              >
                Copy Share Link Token
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateEvent;
